import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useSession } from "../app/session";
import { ROLE_LABEL, type SpaceView } from "../app/views";
import { useT } from "../i18n/index.js";
import { ApiError, api } from "../lib/api";
import { CheckIcon, ChevronDownIcon, PlusIcon, TrashIcon } from "./icons";
import { Button, TextField } from "./ui";
import { cn } from "../lib/cn";

/**
 * A person can belong to several spaces and switches between them (SPEC §4.1).
 * With two or three spaces a dropdown beats a sidebar, and on a phone it is the
 * only thing that fits.
 */
export function SpaceSwitcher() {
  const t = useT();
  const { session, space, selectSpace } = useSession();
  const [open, setOpen] = useState(false);
  /*
    Снимок, а не ссылка на текущее пространство.

    Удаление кончается переключением на другое, и диалог, читающий `space`,
    в этот момент переписал бы себе заголовок на имя соседнего пространства —
    на глазах у человека, который только что подтвердил удаление первого.
  */
  const [doomed, setDoomed] = useState<SpaceView | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!session || !space) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[var(--touch)] items-center gap-1.5 rounded-card px-2 -mx-2 text-left transition-colors hover:bg-surface-alt"
      >
        <span className="min-w-0">
          <span className="block truncate font-display text-lg leading-tight">
            {space.name}
          </span>
          <span className="block text-[0.75rem] leading-tight text-muted">
            {t("settings.memberCount", { count: space.memberCount })}
          </span>
        </span>
        <ChevronDownIcon
          className={cn("size-4 shrink-0 text-muted transition-transform", open && "rotate-180")}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute top-full left-0 z-50 mt-1.5 w-64 overflow-hidden rounded-card bg-surface py-1.5 shadow-soft"
        >
          {/*
            Список прокручивается, а пункт под ним — нет. Пространств может быть
            сколько угодно: своё с девушкой, своё с друзьями, ещё несколько
            чужих, куда позвали. На шести штуках меню перерастает экран телефона,
            и без этого «Новое пространство» уезжает за нижний край — то есть
            пропадает ровно тогда, когда их и становится много.
          */}
          <div className="max-h-[min(60vh,20rem)] overflow-y-auto overscroll-contain">
            {session.spaces.map((s) => (
              <button
                key={s.id}
                role="menuitemradio"
                aria-checked={s.id === space.id}
                onClick={() => {
                  selectSpace(s.id);
                  setOpen(false);
                }}
                className="flex min-h-[var(--touch)] w-full items-center gap-3 px-3 text-left transition-colors hover:bg-surface-alt"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{s.name}</span>
                  <span className="block text-[0.75rem] text-muted">
                    {ROLE_LABEL[s.role]}
                  </span>
                </span>
                {s.id === space.id ? (
                  <CheckIcon className="size-4 shrink-0 text-accent" />
                ) : null}
              </button>
            ))}
          </div>

          {/*
            Пункт, а не подпись. Раньше здесь стояла строка «новое пространство
            создаётся при регистрации» — с тех пор его можно создать самому, и
            искать эту возможность будут именно тут, среди уже имеющихся.
            Сама форма — одна, в настройках: спрашивать название в выпадающем
            меню шапки значит держать второй экземпляр того же самого.
          */}
          {/* Не просто «в настройки»: экран длинный, а форма на нём свёрнута,
              и приход без просьбы выглядел бы как ничего не сделавшая кнопка.
              Настройки эту просьбу исполняют — раскрывают карточку, подводят к
              ней и ставят курсор в поле. */}
          <Link
            to="/settings?new=space"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="mt-1.5 flex min-h-[var(--touch)] items-center gap-3 border-t border-line-soft px-3 text-left text-muted transition-colors hover:bg-surface-alt"
          >
            <PlusIcon className="size-4 shrink-0" strokeWidth={1.8} />
            <span className="min-w-0 flex-1 truncate">{t("settings.spaceNew")}</span>
          </Link>

          {/*
            Одно «удалить», про текущее пространство, а не по кнопке в каждой
            строке списка. Две причины, и обе про телефон: строка списка — это
            цель шириной в палец, чья работа «перейти сюда», и вторая цель
            внутри неё промахивается ровно тогда, когда торопятся; а удалять
            имеет смысл то, на что смотришь — название в этом диалоге придётся
            печатать, и оно то же самое, что стоит в шапке.

            Цветом, а не только словом: рядом с «новым пространством» это
            соседство, и единственное, что его разрывает, — что этот пункт
            выглядит иначе.
          */}
          {space.role === "owner" ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setDoomed(space);
              }}
              className="flex min-h-[var(--touch)] w-full items-center gap-3 border-t border-line-soft px-3 text-left text-neg transition-colors hover:bg-neg-soft"
            >
              <TrashIcon className="size-4 shrink-0" strokeWidth={1.8} />
              <span className="min-w-0 flex-1 truncate">{t("settings.spaceDelete")}</span>
            </button>
          ) : null}
        </div>
      ) : null}

      {doomed ? (
        <DeleteSpaceDialog space={doomed} onClose={() => setDoomed(null)} />
      ) : null}
    </div>
  );
}

/**
 * Подтверждение, которое нельзя пройти не глядя.
 *
 * Двух нажатий здесь мало. «Удалить?» — «Да» проходится большим пальцем на
 * ходу, а цена ошибки — вся общая переписка, списки и календарь за годы, у
 * обоих участников и без возврата. Поэтому название печатают руками: это
 * единственное подтверждение, которое нельзя дать, не прочитав, что удаляешь.
 */
function DeleteSpaceDialog({
  space,
  onClose,
}: {
  space: SpaceView;
  onClose: () => void;
}) {
  const t = useT();
  const { session, selectSpace, refresh } = useSession();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Не автофокус ради удобства: поле — это и есть та работа, которую диалог
    // просит сделать, и человек должен увидеть её курсором, а не искать.
    fieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  // Куда уйти после удаления. Пусто — значит это последнее пространство, и
  // удалять его нечем: сервер откажет, а приложение без пространства не живёт.
  const escape = session?.spaces.find((s) => s.id !== space.id) ?? null;
  const others = Math.max(space.memberCount - 1, 0);

  // Точное совпадение, только по краям обрезанное: пробел, приклеенный
  // автодополнением телефона, — не то, из-за чего стоит отказывать.
  const confirmed = typed.trim() === space.name.trim();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!confirmed || !escape || busy) return;

    setBusy(true);
    setError(null);

    try {
      await api.deleteSpace(space.id);
    } catch (cause) {
      // Сообщение сервера как есть: отказ здесь — это состояние («это ваше
      // единственное пространство»), которое надо прочитать, а не повторить.
      setError(cause instanceof ApiError ? cause.message : t("settings.spaceDeleteFailed"));
      setBusy(false);
      return;
    }

    // Сначала уйти, потом перечитать: `refresh` выбирает пространство по тому,
    // что лежит в localStorage, а кладёт туда как раз `selectSpace`. Без этого
    // приложение секунду стоит в том, чего больше нет.
    selectSpace(escape.id);

    // Список не перечитался — не повод держать диалог открытым: пространства
    // уже нет, мы уже в другом, а список поправится следующим `me`.
    await refresh().catch(() => {});

    onClose();
  }

  return createPortal(
    <>
      {/* Чёрным, а не токеном: затемнение должно читаться как тень в обеих
          темах, а все поверхности между ними меняются местами. */}
      <div
        onClick={busy ? undefined : onClose}
        aria-hidden="true"
        className="fixed inset-0 z-50 bg-black/45"
      />

      <div className="pad-safe-x pad-safe-top pointer-events-none fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain px-4 py-8">
        <form
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-space-title"
          onSubmit={submit}
          className="pointer-events-auto flex w-full max-w-md flex-col gap-4 rounded-card bg-surface p-5 shadow-soft"
        >
          <h2 id="delete-space-title" className="font-display text-[1.0625rem] text-neg">
            {t("settings.spaceDeleteTitle", { space: space.name })}
          </h2>

          {escape ? (
            <>
              <div className="flex flex-col gap-2 text-[0.9375rem] leading-snug text-ink-soft">
                <p>{t("settings.spaceDeleteWhat")}</p>
                {others > 0 ? <p>{t("settings.spaceDeleteOthers", { count: others })}</p> : null}
                <p className="text-neg">{t("settings.spaceDeleteForever")}</p>
              </div>

              <TextField
                ref={fieldRef}
                label={t("settings.spaceDeleteConfirmLabel")}
                hint={t("settings.spaceDeleteConfirmHint", { space: space.name })}
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                error={error ?? undefined}
                disabled={busy}
                // Телефон охотно поправит первую букву и подставит своё —
                // и человек будет смотреть на верное название, которое не
                // совпадает.
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="done"
              />

              <div className="flex gap-2">
                <Button type="button" variant="secondary" full onClick={onClose} disabled={busy}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" variant="danger" full disabled={!confirmed || busy}>
                  {busy ? t("settings.spaceDeleting") : t("settings.spaceDeleteSubmit")}
                </Button>
              </div>
            </>
          ) : (
            /* Отказ объяснён здесь, а не после нажатия: пункт в меню виден
               владельцу всегда, потому что вопрос «как удалить пространство»
               задаёт как раз тот, у кого оно одно. */
            <>
              <p className="text-[0.9375rem] leading-snug text-ink-soft">
                {t("settings.spaceDeleteLast")}
              </p>
              <Button type="button" variant="secondary" full onClick={onClose}>
                {t("common.close")}
              </Button>
            </>
          )}
        </form>
      </div>
    </>,
    document.body,
  );
}
